import { useState, useEffect, useMemo, Fragment } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRoosterStore } from '../store/useRoosterStore.js';
import { haalTeamRooster } from '../lib/api.js';
import { berekenFte } from '../lib/fte.js';
// Pure opmaakhelpers — in lib/ zodat de diagnostiek exact dezelfde labels kan narekenen
// als wat hier gerenderd wordt (node kan geen .jsx importeren). Zie lib/fteLabels.js.
import { afwLabel, uur, afwSamenvatting, emmer } from '../lib/fteLabels.js';

const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

// Sobere, gecentreerde melding in de bestaande kaart-stijl (spiegelt TeamRooster).
function Melding({ emoji, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
      <div className="text-4xl mb-3">{emoji}</div>
      {children}
    </div>
  );
}

// "Danny (verlof, 2×)" bij meervoudige uitval, "Jermaine (ziek)" bij één keer. De kolom
// Ineffectief telt DIENSTEN, dus zonder die telling loopt de regel niet gelijk met het getal
// erboven: Danny kan in dezelfde week twee diensten van dezelfde gemeente missen.
const uitgevallenTekst = (afwezigen) => Object.entries(afwezigen || {})
  .sort((a, b) => a[0].localeCompare(b[0], 'nl'))
  .map(([naam, a]) => `${naam} (${afwLabel(a.type)}${a.aantal > 1 ? `, ${a.aantal}×` : ''})`)
  .join(', ');

export default function FteTab() {
  const { regels, afwezigheid, vakanties, bekekenMaand, maandSelectie, zetBekekenMaand } = useRoosterStore();

  // Maand-peiler: init uit bekekenMaand (overleeft navigatie), anders maandSelectie, anders nu.
  // Zelfde patroon als TeamRooster; de knoppen schrijven ook naar de store.
  const [peiler, setPeiler] = useState(() => {
    if (bekekenMaand) return { jaar: bekekenMaand.jaar, maand: bekekenMaand.maand };
    if (maandSelectie) return { jaar: maandSelectie.jaar, maand: maandSelectie.maand };
    const nu = new Date();
    return { jaar: nu.getFullYear(), maand: nu.getMonth() + 1 };
  });
  const { jaar, maand } = peiler;

  const vorigeMaand = () => {
    const nieuw = { jaar: maand === 1 ? jaar - 1 : jaar, maand: maand === 1 ? 12 : maand - 1 };
    setPeiler(nieuw);
    zetBekekenMaand(nieuw.jaar, nieuw.maand);
  };
  const volgendeMaand = () => {
    const nieuw = { jaar: maand === 12 ? jaar + 1 : jaar, maand: maand === 12 ? 1 : maand + 1 };
    setPeiler(nieuw);
    zetBekekenMaand(nieuw.jaar, nieuw.maand);
  };

  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState(null);

  // Haal de server-momentopname (incl. goedgekeurde ruils) zodra de maand wijzigt. Beheer draait
  // uitsluitend voor de ingelogde teamleider, dus geen aparte auth-gate nodig. setState in een
  // async fn (niet direct in de effect-body), zelfde patroon als TeamRooster.
  useEffect(() => {
    let geannuleerd = false;
    const laad = async () => {
      setLaden(true);
      setFout(null);
      setData(null);
      try {
        const res = await haalTeamRooster(jaar, maand);
        if (!geannuleerd) setData(res);
      } catch (err) {
        if (!geannuleerd) setFout(err.message);
      } finally {
        if (!geannuleerd) setLaden(false);
      }
    };
    laad();
    return () => { geannuleerd = true; };
  }, [jaar, maand]);

  const maandLabel = `${MAANDEN[maand - 1]} ${jaar}`;

  // FTE per week; leeg zolang de maand niet volledig gepubliceerd is (gepubliceerd=false of
  // lege weken -> de server geeft niets terug voor een niet-volledig-vastgelegde maand).
  const weken = useMemo(() => {
    if (!data || data.gepubliceerd === false || !Array.isArray(data.weken) || data.weken.length === 0) return [];
    // Geen deler meer als argument: fte.js leidt hem af uit de actieve gemeenten van deze lade.
    return berekenFte(data, regels, afwezigheid, vakanties);
  }, [data, regels, afwezigheid, vakanties]);

  // Alle gemeenten die voorkomen, op displaynaam gesorteerd (elke week bevat dezelfde set).
  const gemeenteLijst = useMemo(() => {
    const keys = new Set();
    for (const w of weken) for (const k of Object.keys(w.gemeenten)) keys.add(k);
    return [...keys].sort((a, b) =>
      (regels.gemeenten?.[a]?.naam || a).localeCompare(regels.gemeenten?.[b]?.naam || b, 'nl'));
  }, [weken, regels]);

  // Maandgemiddelde FTE per gemeente (gemiddelde over de weken, 1 decimaal). Effectief = g.fte;
  // ineffectief = g.ineffectiefUren / 36 (in de component afgeleid, fte.js ongewijzigd).
  const maandGemFte = useMemo(() => {
    const res = {};
    const n = weken.length;
    for (const k of gemeenteLijst) {
      const somEff = weken.reduce((s, w) => s + (w.gemeenten[k]?.fte || 0), 0);
      const somIneff = weken.reduce((s, w) => s + ((w.gemeenten[k]?.ineffectiefUren || 0) / 36), 0);
      res[k] = {
        eff: n ? Math.round(somEff / n * 10) / 10 : 0,
        ineff: n ? Math.round(somIneff / n * 10) / 10 : 0,
      };
    }
    return res;
  }, [weken, gemeenteLijst]);

  const gemNaam = (k) => regels.gemeenten?.[k]?.naam || k;

  return (
    <div className="space-y-6">
      {/* Maand-navigatie (zelfde als TeamRooster) */}
      <div className="flex items-center gap-2">
        <button
          onClick={vorigeMaand}
          aria-label="Vorige maand"
          className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-medium text-slate-700 capitalize min-w-[9rem] text-center">
          {MAANDEN[maand - 1]} {jaar}
        </span>
        <button
          onClick={volgendeMaand}
          aria-label="Volgende maand"
          className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {laden && <Melding emoji="⏳"><p>FTE-overzicht laden…</p></Melding>}

      {!laden && fout && <Melding emoji="⚠️"><p>{fout}</p></Melding>}

      {!laden && !fout && data && weken.length === 0 && (
        <Melding emoji="🔒">
          <p>Het FTE-overzicht voor {maandLabel} is nog niet beschikbaar.</p>
          <p className="text-xs mt-2 text-slate-400">
            De maand is nog niet volledig vastgelegd. Zodra alle weken zijn vastgelegd, verschijnt het overzicht hier.
          </p>
        </Melding>
      )}

      {!laden && !fout && weken.length > 0 && (
        <div className="space-y-4">
          {weken.map((week) => (
            <div key={`${week.isoJaar}-${week.weeknummer}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-brand-100 bg-brand-50">
                <span className="font-semibold text-brand-800">Week {week.weeknummer}</span>
              </div>
              {/* Onder lg mag de tabel horizontaal scrollen, zoals de matrix. */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" style={{ minWidth: 720 }}>
                  <thead>
                    {/* Groepskop: maakt zichtbaar welke kolommen diensten tellen en welke uren zijn. */}
                    <tr className="text-slate-500 text-xs">
                      <th className="pl-5 pr-3" />
                      <th colSpan={3} className="text-center pt-2.5 pb-1 px-3 font-medium">Diensten (× 8u)</th>
                      <th colSpan={2} className="text-center pt-2.5 pb-1 px-3 font-medium">Uren</th>
                      <th colSpan={2} className="text-center pt-2.5 pb-1 pr-5 pl-3 font-medium">FTE</th>
                    </tr>
                    <tr className="border-b border-slate-100 text-slate-500">
                      <th className="text-left py-2.5 pl-5 pr-3 font-medium">Gemeente</th>
                      <th className="text-right py-2.5 px-3 font-medium">Ingepland</th>
                      <th className="text-right py-2.5 px-3 font-medium">Effectief</th>
                      <th className="text-right py-2.5 px-3 font-medium">Ineffectief</th>
                      <th className="text-right py-2.5 px-3 font-medium">Kantoor</th>
                      <th className="text-right py-2.5 px-3 font-medium">School</th>
                      <th className="text-right py-2.5 px-3 font-medium">FTE eff.</th>
                      <th className="text-right py-2.5 pr-5 pl-3 font-medium">FTE ineff.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gemeenteLijst.map((gemKey) => {
                      const g = week.gemeenten[gemKey];
                      if (!g) return null;
                      const afwEntries = Object.entries(g.afwezigen || {})
                        .sort((a, b) => a[0].localeCompare(b[0], 'nl'));
                      return (
                        <Fragment key={gemKey}>
                          <tr className="border-t border-slate-100">
                            <td className="py-2.5 pl-5 pr-3 whitespace-nowrap text-brand-700 font-semibold">{gemNaam(gemKey)}</td>
                            <td className="text-right py-2.5 px-3 text-slate-700">{g.ingepland}</td>
                            <td className="text-right py-2.5 px-3 text-slate-700">{g.effectief}</td>
                            <td className="text-right py-2.5 px-3 text-slate-700">{g.ineffectief}</td>
                            <td className="text-right py-2.5 px-3 text-slate-700">{g.kantoorUren.toFixed(1)}u</td>
                            <td className="text-right py-2.5 px-3 text-slate-700">{g.schoolUren.toFixed(1)}u</td>
                            <td className="text-right py-2.5 px-3 font-bold text-slate-800">{g.fte.toFixed(1)}</td>
                            <td className="text-right py-2.5 pr-5 pl-3 font-bold text-slate-800">{(g.ineffectiefUren / 36).toFixed(1)}</td>
                          </tr>
                          {afwEntries.length > 0 && (
                            <tr>
                              <td colSpan={8} className="pl-5 pr-5 pb-2.5 text-xs text-slate-400">
                                Uitgevallen: {uitgevallenTekst(g.afwezigen)}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Teamcapaciteit ONDER de tabel: dit gaat over het hele team, niet over wat een
                  gemeente geleverd kreeg. Boven de tabel las het als een kop bij de gemeenterijen
                  en dat is het niet. Twee blokken: eerst de drie getallen, dan de namen —
                  gesplitst naar wél of niet ingepland op de dag dat ze wegvielen. */}
              {week.team && (
                <div className="border-t border-slate-100">
                  <div className="px-5 py-2.5 text-xs text-slate-500">
                    Capaciteit{' '}
                    <span className="font-semibold text-slate-700">
                      {week.team.aantal} mensen / {uur(week.team.capaciteitUren)}
                    </span>
                    {' · '}Niet inzetbaar{' '}
                    <span className="font-semibold text-slate-700">{uur(week.team.nietInzetbaarUren)}</span>
                    {' · '}Inzetbaar{' '}
                    <span className="font-semibold text-slate-700">{uur(week.team.inzetbaarUren)}</span>
                  </div>
                  {/* Twee lijstjes in plaats van één. De oude kop "Niet toegerekend aan een
                      gemeente" is vervallen: hij gold alleen voor de eerste groep. De tweede
                      groep stond wél in een dienstcel en staat hierboven al als ineffectieve
                      dienst bij een gemeente — dezelfde dagen, twee keer op één scherm.
                      De labels zeggen alleen wat er GEMETEN is (stond die dag in een dienst,
                      ja of nee) en nooit waarom: dat een dienstcel leeg bleef kan net zo goed
                      betekenen dat er die dag genoeg anderen waren. */}
                  {(() => {
                    const nietIngepland = emmer(week.team.afwezigen, 'urenNietIngepland', 'typenNietIngepland');
                    const uitgevallen = emmer(week.team.afwezigen, 'urenUitgevallen', 'typenUitgevallen');
                    if (!Object.keys(nietIngepland).length && !Object.keys(uitgevallen).length) return null;
                    return (
                      <div className="px-5 pb-3 text-xs text-slate-500 space-y-1.5">
                        {Object.keys(nietIngepland).length > 0 && (
                          <div>
                            <div className="font-medium text-slate-600">Niet ingepland</div>
                            <div className="text-slate-500">
                              {afwSamenvatting(nietIngepland)}
                              {' — samen '}
                              <span className="font-semibold text-slate-700">{uur(week.team.nietIngeplandUren)}</span>
                            </div>
                          </div>
                        )}
                        {Object.keys(uitgevallen).length > 0 && (
                          <div>
                            <div className="font-medium text-slate-600">Wel ingepland, uitgevallen</div>
                            <div className="text-slate-500">
                              {afwSamenvatting(uitgevallen)}
                              {' — samen '}
                              <span className="font-semibold text-slate-700">{uur(week.team.uitgevallenUren)}</span>
                            </div>
                          </div>
                        )}
                        <div className="text-slate-400" style={{ fontSize: 11 }}>
                          &quot;Niet ingepland&quot; = deze persoon stond die dag in geen enkele dienst.
                          &quot;Wel ingepland, uitgevallen&quot; = deze uren staan hierboven ook als
                          ineffectieve dienst bij een gemeente.
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ))}

          {/* Maandgemiddelde FTE per gemeente */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-brand-100 bg-brand-50">
              <span className="font-semibold text-brand-800">Maandgemiddelde</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" style={{ minWidth: 440 }}>
                <thead>
                  <tr className="border-b border-slate-100 text-slate-500">
                    <th className="text-left py-2.5 pl-5 pr-3 font-medium">Gemeente</th>
                    <th className="text-right py-2.5 px-3 font-medium">FTE eff.</th>
                    <th className="text-right py-2.5 pr-5 pl-3 font-medium">FTE ineff.</th>
                  </tr>
                </thead>
                <tbody>
                  {gemeenteLijst.map((gemKey) => (
                    <tr key={gemKey} className="border-t border-slate-100">
                      <td className="py-2.5 pl-5 pr-3 text-brand-700 font-semibold">{gemNaam(gemKey)}</td>
                      <td className="text-right py-2.5 px-3 font-bold text-slate-800">{(maandGemFte[gemKey]?.eff ?? 0).toFixed(1)}</td>
                      <td className="text-right py-2.5 pr-5 pl-3 font-bold text-slate-800">{(maandGemFte[gemKey]?.ineff ?? 0).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
